import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import type { ThemeName } from "../../theme";
import type { ClassNameProp } from "../../props";

import { logoStyle } from "./Logo.css";

export type LogoProps = RecipeVariants<typeof logoStyle> &
  ClassNameProp & {
    theme?: ThemeName;
    gradient?: boolean;
  };

/**
 * Logo component.
 */
export function Logo({
  size,
  theme,
  gradient,
  alignSelf,
  className,
  ...props
}: LogoProps) {
  switch (theme) {
    case "unive":
    case "deHypotheker":
    case "ing":
    case "rabobank":
    case "nationaleNederlanden":
    case "vanLanschotKempen":
    case "verenigingEigenHuis":
    case "quion":
    case "nibc":
    case "hypotheekvisie":
    case "vista":
    case "asr":
    case "anwb":
    case "eviVanLanschot":
    case "allianz":
    case "fnv":
    case "randstad":
    case "achmea":
    case "funda":
    case "nvm":
    case "abnamro":
    case "domek":
      return (
        <img
          src={`/assets/images/logos/${theme}.svg`}
          className={mergeClasses(logoStyle({ size, alignSelf }), className)}
          alt={`${theme} logo`}
          draggable={false}
        />
      );
    case "munt":
    case "obvion":
      return (
        <img
          src={`/assets/images/logos/${theme}.png`}
          className={mergeClasses(logoStyle({ size, alignSelf }), className)}
          alt={`${theme} logo`}
          draggable={false}
        />
      );

    default:
      return (
        <svg
          role="img"
          height="27"
          viewBox="0 0 114 27"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={mergeClasses(logoStyle({ size, alignSelf }), className)}
          aria-label="Huub logo"
          {...props}
        >
          {gradient && (
            <defs>
              <linearGradient
                id="gradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
                gradientTransform="rotate(-40)"
              >
                <stop offset="0%" stopColor="#0E83FF" />
                <stop offset="33%" stopColor="#C467D5" />
                <stop offset="66%" stopColor="#FF6B6E" />
              </linearGradient>
            </defs>
          )}
          <path
            d="M9.54395 1.32129C10.4231 0.661886 11.4882 0.399774 12.5166 0.533203C13.0893 0.607203 13.3692 1.29486 13.0234 1.77051L11.9355 3.26953C11.7849 3.47519 12 3.75104 12.2285 3.64746L15.5635 2.12793C15.6381 2.09341 15.7264 2.1018 15.7949 2.15039H15.7979C15.9152 2.23046 15.9431 2.39358 15.8594 2.50977L14.2178 4.77344C14.0671 4.97907 14.2823 5.25576 14.5107 5.15234L16.9482 4.04004C17.5211 3.77785 18.186 3.85214 18.6934 4.23047L18.708 4.24121C22.2169 6.86484 21.9824 6.9707 21.9824 6.9707C23.0564 7.77605 23.6922 9.06138 23.6924 10.4287C23.6991 10.4293 23.9805 10.4835 23.9971 15.6514L23.998 15.6533C24.0147 20.8544 23.6968 21.5802 23.6934 21.5879C23.6933 23.954 21.83 25.8711 19.5293 25.8711C19.5189 25.8841 18.9879 26.5037 11.9111 26.499C4.8266 26.4943 4.47473 25.8826 4.46875 25.8711C2.16964 25.8711 0.304727 23.954 0.304688 21.5879C0.301872 21.5717 0.0151616 19.9094 0 15.2363C-0.0151236 10.5671 0.301138 10.432 0.304688 10.4307C0.30474 9.06321 0.939598 7.77868 2.01367 6.97168C2.00754 6.96756 1.95541 6.86298 5.05371 4.41895C8.24095 1.90356 9.53764 1.3241 9.54395 1.32129ZM60.875 17.627C60.875 18.9282 61.1415 19.91 61.6748 20.5713C62.2295 21.2325 63.0726 21.5625 64.2031 21.5625C64.9282 21.5624 65.5686 21.4029 66.123 21.083C66.6774 20.7417 67.1147 20.261 67.4346 19.6426C67.7757 19.0027 67.9463 18.2237 67.9463 17.3066V8.69922H72.2344V24.8271H68.459L68.1064 22.2031C67.6372 23.1202 66.9434 23.8566 66.0264 24.4111C65.1092 24.9443 64.021 25.2109 62.7627 25.2109C61.4614 25.2109 60.341 24.9444 59.4023 24.4111C58.4851 23.8565 57.7816 23.056 57.291 22.0107C56.8004 20.9442 56.5547 19.6323 56.5547 18.0752V8.69922H60.875V17.627ZM80.5938 17.627C80.5938 18.9282 80.8603 19.91 81.3936 20.5713C81.9482 21.2325 82.7913 21.5625 83.9219 21.5625C84.6469 21.5624 85.2873 21.4029 85.8418 21.083C86.3962 20.7417 86.8334 20.261 87.1533 19.6426C87.4944 19.0027 87.665 18.2237 87.665 17.3066V8.69922H91.9531V24.8271H88.1777L87.8252 22.2031C87.3559 23.1202 86.6621 23.8566 85.7451 24.4111C84.828 24.9443 83.7398 25.2109 82.4814 25.2109C81.1802 25.2109 80.0597 24.9444 79.1211 24.4111C78.2039 23.8565 77.5004 23.056 77.0098 22.0107C76.5192 20.9442 76.2735 19.6323 76.2734 18.0752V8.69922H80.5938V17.627ZM100.504 10.9385C101.016 10.1921 101.689 9.57356 102.521 9.08301C103.374 8.57123 104.43 8.31445 105.688 8.31445C107.182 8.31455 108.504 8.68807 109.656 9.43457C110.829 10.1599 111.747 11.1635 112.408 12.4434C113.091 13.7019 113.433 15.1525 113.433 16.7949C113.433 18.3947 113.091 19.8353 112.408 21.1152C111.747 22.3949 110.84 23.3978 109.688 24.123C108.537 24.8483 107.214 25.2108 105.721 25.2109C104.889 25.2109 104.142 25.1039 103.48 24.8906C102.84 24.6773 102.264 24.39 101.752 24.0273C101.261 23.6433 100.845 23.2164 100.504 22.7471L100.024 24.8271H96.1846V1.78711H100.504V10.9385ZM38.3193 11.6426H48.1113V2.42676H52.4307V24.8271H48.1113V15.1309H38.3193V24.8271H33.999V2.42676H38.3193V11.6426ZM104.696 12.0908C103.864 12.0908 103.117 12.2941 102.456 12.6992C101.816 13.0831 101.304 13.6274 100.92 14.3311C100.557 15.0348 100.376 15.8457 100.376 16.7627C100.376 17.6798 100.557 18.5014 100.92 19.2266C101.304 19.9306 101.816 20.4754 102.456 20.8594C103.117 21.2433 103.864 21.4346 104.696 21.4346C105.549 21.4345 106.297 21.2433 106.937 20.8594C107.598 20.4541 108.11 19.9091 108.473 19.2266C108.835 18.5228 109.017 17.7119 109.017 16.7949C109.017 15.8779 108.835 15.0671 108.473 14.3633C108.11 13.6382 107.598 13.0832 106.937 12.6992C106.297 12.2939 105.549 12.0909 104.696 12.0908Z"
            fill={gradient ? "url(#gradient)" : "currentColor"}
          />
        </svg>
      );
  }
}
